import { Separator, Button } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useZionAxios } from '@fs/zion-axios'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { type GroupSummary, type TreeGroupDetails } from '@fs/zion-tree-types'
import { type Contributions } from '@fs/zion-tree-contributions'
import GroupCard from '../../GroupCard'
import Table, { TableSkeleton } from '../contributions/Table'
import { useGroupManagementGroupContext } from '../../../../providers/GroupManagementGroupProvider'
import HiddenTreeMessage from '../HiddenTreeMessage'

const CHANGE_LOG_PAGE_SIZE = 5

export default function TreeChangesCard(): JSX.Element | null {
  const { group } = useGroupManagementGroupContext() ?? {}
  const { treeDetails } = useGroupsContext()
  const isHidden = treeDetails?.hidden
  const treeId = group?.treeIds?.[0]

  if (isHidden) {
    return (
      <GroupCard cardProps={{ 'data-testid': 'tree-change-log-hidden-card' }}>
        <HiddenTreeMessage />
      </GroupCard>
    )
  }

  if (!treeId) return null

  return <TreeChangesContent group={group} />
}

type TreeChangesContentProps = {
  group: TreeGroupDetails | GroupSummary | undefined
}

export function TreeChangesContent({ group }: TreeChangesContentProps): JSX.Element {
  const [t] = useTranslation()
  const treeId = group?.treeIds?.[0]
  const [{ data, error }] = useZionAxios<Contributions>(
    `/service/tree/tree-data/user/all/tree/${treeId}/contributions?pageSize=${CHANGE_LOG_PAGE_SIZE}`
  )

  return (
    <GroupCard
      heading={t('group-management.latest-changes.heading')}
      cardProps={{ 'data-testid': 'latest-changes-card' }}
    >
      <Table
        contributionList={data?.contributions}
        loading={!data?.contributions && !error}
        error={error}
        contributionsPersons={data?.persons}
      />
      <Separator size="xxs" />
      {group && (
        <Button emphasis="medium" data-testid="group-changelog-action" to={`/research-tree/${group.id}/changes`}>
          {t('group-management.show-all.action')}
        </Button>
      )}
    </GroupCard>
  )
}

export function TreeChangesContentSkeleton(): JSX.Element {
  const [t] = useTranslation()
  return (
    <GroupCard
      heading={t('group-management.latest-changes.heading')}
      cardProps={{ 'data-testid': 'latest-changes-card-skeleton' }}
    >
      <TableSkeleton />
    </GroupCard>
  )
}
